/**
 * Que pasa si aprieto Aplicar, ANTES de apretarlo.
 *
 * LA REGLA QUE ESTE PANEL HACE CUMPLIR
 * ------------------------------------
 * "Aplicar" es un boton que reescribe el costo de cientos de productos y dispara
 * el recalculo del precio de venta de cada uno. Nadie puede decidir eso mirando
 * una tabla de 400 filas. El panel lo baja a cinco numeros:
 *
 *   · cuantos productos cambian de costo y cuantos se dan de alta
 *   · cuanto sube el costo total, POR MONEDA (nunca sumadas)
 *   · a cuantos les sube el precio de venta y a cuantos les baja
 *   · cuantas filas conviene mirar antes de confirmar
 *
 * LOS PESOS Y LOS DOLARES NO SE SUMAN
 * -----------------------------------
 * Cada moneda tiene su renglon. El consolidado en pesos existe porque el dueno
 * piensa en pesos, pero va abajo, con la cotizacion que uso escrita al lado:
 * un total mixto sin cotizacion visible es un numero que no se puede auditar.
 */
import type { ReactNode } from "react";
import { ArrowDown, ArrowUp, TriangleAlert } from "lucide-react";
import { formatear, formatearCotizacion } from "@tora/shared";

import { Importe } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { cn } from "@/lib/utils";

import { BarraAcciones } from "./BarraAcciones";
import { consolidarEnArs, formatearVariacion, type Impacto } from "./logica";

export interface PanelImpactoProps {
  impacto: Impacto;
  cotizacion: number;
  /**
   * El boton que corresponda: aplicar mientras se revisa, revertir despues.
   *
   * Va adentro del panel y no solo en la barra de abajo porque el panel queda
   * pegado arriba en escritorio y primero en mobile: es el unico lugar donde el
   * boton esta siempre al lado de los numeros que lo justifican.
   */
  accion?: ReactNode;
  /** El import ya se aplico: el panel deja de hablar en condicional. */
  aplicado?: boolean;
}

export function PanelImpacto({
  impacto,
  cotizacion,
  accion,
  aplicado = false,
}: PanelImpactoProps): JSX.Element {
  const consolidado = consolidarEnArs(impacto, cotizacion);
  const aplicables = impacto.actualizan + impacto.crean;

  return (
    <Card padding="normal" className="space-y-4">
      <TituloSeccion
        descripcion={
          aplicado
            ? "Ya está escrito. Se deshace entero con Revertir."
            : aplicables === 0
              ? "No hay ninguna fila marcada. Nada se escribiría."
              : "Nada de esto está escrito todavía. Se escribe recién al confirmar."
        }
      >
        {aplicado ? "Lo que se aplicó" : "Si aplicás ahora"}
      </TituloSeccion>

      <dl className="grid grid-cols-2 gap-3">
        <Contador titulo="Cambian de costo" valor={impacto.cambianCosto} />
        <Contador titulo="Se dan de alta" valor={impacto.crean} />
        {/* Las que se actualizan con el mismo costo tambien quedan como estan. */}
        <Contador titulo="Quedan como están" valor={impacto.ignoran + impacto.sinCambio} tenue />
        <Contador
          titulo="Conviene mirarlas"
          valor={impacto.sospechosas}
          alerta={impacto.sospechosas > 0}
        />
      </dl>

      {impacto.porMoneda.length > 0 ? (
        <div className="space-y-2 border-t border-border pt-3">
          <TituloSeccion nivel="etiqueta">Costo total de lo que se actualiza</TituloSeccion>
          {impacto.porMoneda.map((m) => (
            <div key={m.moneda} className="flex items-baseline justify-between gap-3">
              <span className="text-xs text-muted-foreground">
                {m.filas} {m.filas === 1 ? "producto" : "productos"} en {m.moneda}
              </span>
              <span className="flex flex-col items-end gap-0.5">
                <span className="flex items-baseline gap-2">
                  <Importe
                    monto={m.costoAnterior}
                    moneda={m.moneda}
                    tamano="sm"
                    codigo={false}
                    className="line-through"
                  />
                  <Importe monto={m.costoNuevo} moneda={m.moneda} tamano="sm" />
                </span>
                <Delta delta={m.delta} pct={m.pct} moneda={m.moneda} />
              </span>
            </div>
          ))}

          {consolidado && impacto.porMoneda.length > 1 ? (
            <div className="flex items-baseline justify-between gap-3 border-t border-border pt-2">
              <span className="text-xs text-muted-foreground">
                Todo junto, a US$ 1 = $ {formatearCotizacion(consolidado.cotizacion)}
              </span>
              <Delta delta={consolidado.delta} pct={consolidado.pct} moneda="ARS" />
            </div>
          ) : null}
        </div>
      ) : null}

      {impacto.subenPrecio > 0 || impacto.bajanPrecio > 0 ? (
        <div className="flex flex-wrap gap-x-4 gap-y-1 border-t border-border pt-3 text-xs">
          <span className="text-muted-foreground">Precio de venta:</span>
          {impacto.subenPrecio > 0 ? (
            <span className="inline-flex items-center gap-1 text-destructive">
              <ArrowUp className="h-3 w-3" aria-hidden="true" />
              sube en {impacto.subenPrecio}
            </span>
          ) : null}
          {impacto.bajanPrecio > 0 ? (
            <span className="inline-flex items-center gap-1 text-success">
              <ArrowDown className="h-3 w-3" aria-hidden="true" />
              baja en {impacto.bajanPrecio}
            </span>
          ) : null}
        </div>
      ) : null}

      {impacto.cambiosDeMoneda > 0 ? (
        <Aviso tono="destructive" icono={TriangleAlert} compacto>
          {impacto.cambiosDeMoneda}{" "}
          {impacto.cambiosDeMoneda === 1
            ? "producto cambia la moneda de su costo"
            : "productos cambian la moneda de su costo"}
          . No entran en los totales de arriba porque no hay forma honesta de compararlos.
        </Aviso>
      ) : null}

      {impacto.invalidas > 0 ? (
        <Aviso tono="destructive" icono={TriangleAlert} compacto>
          {impacto.invalidas}{" "}
          {impacto.invalidas === 1
            ? "fila está marcada Actualizar sin producto asociado"
            : "filas están marcadas Actualizar sin producto asociado"}
          . Resolvelas antes de aplicar.
        </Aviso>
      ) : null}

      {accion ? <div className="border-t border-border pt-3">{accion}</div> : null}
    </Card>
  );
}

function Contador({
  titulo,
  valor,
  tenue = false,
  alerta = false,
}: {
  titulo: string;
  valor: number;
  tenue?: boolean;
  alerta?: boolean;
}): JSX.Element {
  return (
    <div className="min-w-0">
      <dt className="truncate text-xs text-muted-foreground">{titulo}</dt>
      <dd
        className={cn(
          "text-2xl font-semibold tabular-nums",
          tenue && "text-muted-foreground",
          alerta && "text-warning",
        )}
      >
        {valor}
      </dd>
    </div>
  );
}

function Delta({
  delta,
  pct,
  moneda,
}: {
  delta: number;
  pct: number | null;
  moneda: "ARS" | "USD";
}): JSX.Element {
  const sube = delta > 0.009;
  const baja = delta < -0.009;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 whitespace-nowrap text-xs font-medium tabular-nums",
        sube && "text-destructive",
        baja && "text-success",
        !sube && !baja && "text-muted-foreground",
      )}
    >
      {sube ? <ArrowUp className="h-3 w-3" aria-hidden="true" /> : null}
      {baja ? <ArrowDown className="h-3 w-3" aria-hidden="true" /> : null}
      {sube ? "+" : ""}
      {formatear(delta, moneda)}
      {pct === null ? "" : ` (${formatearVariacion(pct)})`}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// La barra de accion
// ─────────────────────────────────────────────────────────────────────────────

export interface BarraAplicarProps {
  impacto: Impacto;
  puedeAplicar: boolean;
  aplicando: boolean;
  onAplicar: () => void;
}

/**
 * Fija abajo mientras se revisa.
 *
 * Con 400 filas, el boton de aplicar queda a diez pantallas de scroll del
 * resumen. La barra lo trae siempre a mano y, sobre todo, trae los dos numeros
 * que importan pegados al boton: cuantos se actualizan y cuantos se crean.
 */
export function BarraAplicar({
  impacto,
  puedeAplicar,
  aplicando,
  onAplicar,
}: BarraAplicarProps): JSX.Element {
  return (
    <BarraAcciones
      sangria="pagina"
      resumen={
        <>
          {/* El estado de la LISTA (MASTER §10): sin aplicar, el catalogo sigue
              con los costos viejos. */}
          <Badge variant="warning">Sin aplicar</Badge>
          <p>
            <span className="font-semibold tabular-nums">{impacto.cambianCosto}</span> cambian de
            costo, <span className="font-semibold tabular-nums">{impacto.crean}</span> se dan de
            alta.
            {impacto.sospechosas > 0 ? (
              <span className="ml-2 text-warning">
                {impacto.sospechosas} conviene mirarlas primero.
              </span>
            ) : null}
          </p>
        </>
      }
    >
      <Button type="button" onClick={onAplicar} disabled={!puedeAplicar} cargando={aplicando}>
        Aplicar la lista
      </Button>
    </BarraAcciones>
  );
}
